import { ChangeDetectorRef, Component, forwardRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControlDirective,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { AppState } from 'src/app/state';
import { SECTION_TYPES } from 'src/app/core/dashboard-constants';
import { Template } from 'src/app/models/template';
import { Section } from 'src/app/models/section';
import { Language } from 'src/app/models/language';
import { templatesTinymceInitConfig } from 'src/app/shared/components/tinymce-editor/tinymce-editor-config';
import {
  selectSelectedLanguage,
  selectSelectedTemplate,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/templates/templates.selectors';
import { setSelectedLanguage } from 'src/app/dashboard/state/templates/templates.actions';
import { removeWhitespaces } from 'src/app/core/custom-form-validators';
import { selectSelectedSection } from 'src/app/dashboard/state/sections/sections.selectors';

const SECTION_TITLE_IDENTIFIER = 'section-title';
const SECTION_TEXT_IDENTIFIER = 'section-text';

@Component({
  selector: 'dashboard-section-dialog-core',
  templateUrl: './section-dialog-core.component.html',
  styleUrls: ['./section-dialog-core.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SectionDialogCoreComponent),
      multi: true,
    },
  ],
})
export class SectionDialogCoreComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild(FormControlDirective, { static: true })
  formControlDirective: FormControlDirective;

  sectionDialogCoreForm: FormGroup;

  selectedTemplate: Template;
  selectedSection: Section;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  isEdit = false;

  types = SECTION_TYPES;
  SECTION_TITLE_IDENTIFIER = SECTION_TITLE_IDENTIFIER;
  SECTION_TEXT_IDENTIFIER = SECTION_TEXT_IDENTIFIER;

  tinymceInitConfig = {
    ...templatesTinymceInitConfig,
    height: 200,
  };

  onChange;
  onTouched;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sectionDialogCoreForm = this.formBuilder.group({
      sectionName: ['', Validators.required],
      type: ['', Validators.required],
      translations: this.formBuilder.array([]),
    });

    this.subscriptions.add(
      this.store.select(selectSelectedSection).subscribe((section) => {
        this.selectedSection = section;
        this.isEdit = !!section;
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedTemplate).subscribe((template) => (this.selectedTemplate = template))
    );

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => {
        this.selectedLanguage = language;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedTemplate).subscribe((template) => (this.selectedTemplate = template))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedSection).subscribe((section) => {
        this.selectedSection = section;
        this.isEdit = !!section;

        this.removeAllTranslationFields();
        if (this.isEdit) {
          this.sectionName.patchValue(this.selectedSection?.sectionName);
          this.type.patchValue(this.selectedSection?.type);
          if (this.supportedLanguages) {
            this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
          }
        } else {
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
        }
      })
    );

    this.sectionDialogCoreForm.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });
  }

  get sectionName(): AbstractControl {
    return this.sectionDialogCoreForm.get('sectionName') as AbstractControl;
  }

  get type(): AbstractControl {
    return this.sectionDialogCoreForm.get('type') as AbstractControl;
  }

  get translations(): FormArray {
    return this.sectionDialogCoreForm.get('translations') as FormArray;
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedSection?.translations?.filter(
      (translation) => translation.language === languageCode
    );

    const sectionTitle = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [SECTION_TITLE_IDENTIFIER],
      translation: [
        filteredTranslations?.length ? filteredTranslations[0]?.translation : '',
        [Validators.maxLength(30), removeWhitespaces],
      ],
    });
    const sectionText = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[1]?.id : 0],
      language: [languageCode],
      identifier: [SECTION_TEXT_IDENTIFIER],
      translation: [
        filteredTranslations?.length ? filteredTranslations[1]?.translation : '',
        [Validators.maxLength(600)],
      ],
    });

    this.translations.push(sectionTitle);
    this.translations.push(sectionText);

    this.writeValue(this.translations);
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    if (value) {
      this.sectionDialogCoreForm.patchValue(value);
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective.valueAccessor.setDisabledState(isDisabled);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
